/**
 * 컴포넌트별 상세 옵션 에디터
 */
import { useTranslation } from 'react-i18next'
import { Lock } from 'lucide-react'
import type { ComponentConfig, ComponentOptions } from '@/types/configuration'
import { DiaryQuizSetSelector } from './DiaryQuizSetSelector'

interface ComponentOptionsEditorProps {
  component: ComponentConfig
  onUpdate: (component: ComponentConfig) => void
  readOnly?: boolean
}

export const ComponentOptionsEditor: React.FC<ComponentOptionsEditorProps> = ({
  component,
  onUpdate,
  readOnly
}) => {
  const { t } = useTranslation('configuration')
  const options = component.options || {}

  if (readOnly) {
    return (
      <div className="text-sm text-gray-500 flex items-center gap-2">
        <Lock className="w-4 h-4 text-gray-400" />
        {t('componentOptions.lockedByParent')}
      </div>
    )
  }

  const updateOption = <K extends keyof ComponentOptions>(
    key: K,
    value: ComponentOptions[K]
  ) => {
    onUpdate({
      ...component,
      options: {
        ...options,
        [key]: value
      }
    })
  }

  // 비디오 옵션
  if (component.type === 'video') {
    return (
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">{t('componentOptions.videoSettings')}</h4>

        <div className="grid grid-cols-2 gap-4">
          {/* 스킵 허용 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.allowSkip ?? false}
              onChange={(e) => updateOption('allowSkip', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">{t('componentOptions.allowSkip')}</span>
          </label>

          {/* 첫 시청 후 스킵 허용 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.allowSkipAfterFirstWatch ?? true}
              onChange={(e) => updateOption('allowSkipAfterFirstWatch', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">{t('componentOptions.allowSkipAfterFirstWatch')}</span>
          </label>

          {/* 배속 재생 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.allowPlaybackSpeed ?? true}
              onChange={(e) => updateOption('allowPlaybackSpeed', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">{t('componentOptions.allowPlaybackSpeed')}</span>
          </label>
        </div>

        {/* 최소 시청률 */}
        <div>
          <label className="block text-sm text-gray-700 mb-1">{t('componentOptions.minWatchPercent')}</label>
          <input
            type="number"
            min={0}
            max={100}
            value={options.requiredWatchPercent ?? 90}
            onChange={(e) => updateOption('requiredWatchPercent', parseInt(e.target.value) || 0)}
            className="w-24 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>
    )
  }

  // 퀴즈 옵션
  if (component.type === 'quiz') {
    return (
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">{t('componentOptions.quizSettings')}</h4>

        <div className="grid grid-cols-2 gap-4">
          {/* 즉시 정답 표시 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.showAnswerImmediately ?? false}
              onChange={(e) => updateOption('showAnswerImmediately', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">{t('componentOptions.showAnswerImmediately')}</span>
          </label>

          {/* 제출 후 정답 표시 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.showAnswerAfterSubmit ?? true}
              onChange={(e) => updateOption('showAnswerAfterSubmit', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">{t('componentOptions.showAnswerAfterSubmit')}</span>
          </label>

          {/* 재시도 허용 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.allowRetry ?? true}
              onChange={(e) => updateOption('allowRetry', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">{t('componentOptions.allowRetry')}</span>
          </label>

          {/* 틀린 문제만 재시도 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.retryIncorrectOnly ?? false}
              onChange={(e) => updateOption('retryIncorrectOnly', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">{t('componentOptions.retryIncorrectOnly')}</span>
          </label>

          {/* 문제 순서 랜덤 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.shuffleQuestions ?? false}
              onChange={(e) => updateOption('shuffleQuestions', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">{t('componentOptions.shuffleQuestions')}</span>
          </label>

          {/* 선택지 순서 랜덤 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.shuffleOptions ?? false}
              onChange={(e) => updateOption('shuffleOptions', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">{t('componentOptions.shuffleOptions')}</span>
          </label>

          {/* 힌트 표시 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.showHint ?? true}
              onChange={(e) => updateOption('showHint', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">{t('componentOptions.showHint')}</span>
          </label>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {/* 최대 시도 횟수 */}
          <div>
            <label className="block text-sm text-gray-700 mb-1">{t('componentOptions.maxAttempts')}</label>
            <input
              type="number"
              min={0}
              value={options.maxAttempts ?? 0}
              onChange={(e) => updateOption('maxAttempts', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="text-xs text-gray-500">{t('componentOptions.unlimitedHint')}</span>
          </div>

          {/* 통과 점수 */}
          <div>
            <label className="block text-sm text-gray-700 mb-1">{t('componentOptions.passingScore')}</label>
            <input
              type="number"
              min={0}
              max={100}
              value={options.passingScore ?? 70}
              onChange={(e) => updateOption('passingScore', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* 시간 제한 */}
          <div>
            <label className="block text-sm text-gray-700 mb-1">{t('componentOptions.timeLimit')}</label>
            <input
              type="number"
              min={0}
              value={options.timeLimitMinutes ?? 0}
              onChange={(e) => updateOption('timeLimitMinutes', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="text-xs text-gray-500">{t('componentOptions.noLimitHint')}</span>
          </div>
        </div>

        {/* 문제 개수 설정 */}
        <div className="border-t pt-4 mt-4">
          <h5 className="text-sm font-medium text-gray-700 mb-3">{t('componentOptions.questionCountSettings')}</h5>
          <div className="grid grid-cols-2 gap-4">
            {/* 바람직한 문제 개수 */}
            <div>
              <label className="block text-sm text-gray-700 mb-1">{t('componentOptions.preferredQuestionCount')}</label>
              <input
                type="number"
                min={0}
                value={options.questionCount ?? 0}
                onChange={(e) => updateOption('questionCount', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
              />
              <span className="text-xs text-gray-500">{t('componentOptions.allQuestionsHint')}</span>
            </div>

            {/* 문제 부족 시 전략 */}
            <div>
              <label className="block text-sm text-gray-700 mb-1">{t('componentOptions.questionShortage')}</label>
              <select
                value={options.questionShortageStrategy ?? 'available_only'}
                onChange={(e) => updateOption('questionShortageStrategy', e.target.value as 'available_only' | 'fill_from_completed')}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="available_only">{t('componentOptions.availableOnly')}</option>
                <option value="fill_from_completed">{t('componentOptions.fillFromCompleted')}</option>
              </select>
              <span className="text-xs text-gray-500">
                {options.questionShortageStrategy === 'fill_from_completed'
                  ? t('componentOptions.fillFromCompletedDesc')
                  : t('componentOptions.availableOnlyDesc')}
              </span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // 과제 옵션
  if (component.type === 'assignment') {
    return (
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">{t('componentOptions.assignmentSettings')}</h4>

        {/* 생각 정리하기 퀴즈 세트 선택 */}
        <DiaryQuizSetSelector
          value={options.diaryQuizSetId}
          onChange={(setId) => updateOption('diaryQuizSetId', setId)}
        />

        <div className="grid grid-cols-2 gap-4">
          {/* 늦은 제출 허용 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.allowLateSubmission ?? false}
              onChange={(e) => updateOption('allowLateSubmission', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">{t('componentOptions.allowLateSubmission')}</span>
          </label>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* 늦은 제출 페널티 */}
          <div>
            <label className="block text-sm text-gray-700 mb-1">{t('componentOptions.latePenaltyPercent')}</label>
            <input
              type="number"
              min={0}
              max={100}
              value={options.latePenaltyPercent ?? 0}
              onChange={(e) => updateOption('latePenaltyPercent', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* 최대 파일 크기 */}
          <div>
            <label className="block text-sm text-gray-700 mb-1">{t('componentOptions.maxFileSizeMb')}</label>
            <input
              type="number"
              min={1}
              value={options.maxFileSizeMb ?? 10}
              onChange={(e) => updateOption('maxFileSizeMb', parseInt(e.target.value) || 10)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>
    )
  }

  // 기타 컴포넌트 (옵션 없음)
  return (
    <div className="text-sm text-gray-500">
      {t('componentOptions.noAdditionalSettings')}
    </div>
  )
}
